import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { CheckoutGetPurchaseOrderResponse, LoanResponse } from '@impact/data';
import { IMPACT_OVERLAY_DATA, ImpactOverlayRef } from '@impactdk/ngx-overlay';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { closeLightIcon } from '../../../icons';
import { BreakpointService } from '../../breakpoint/breakpoint.service';
import { CHECKOUT_STEP, CheckoutFlowService } from '../../checkout/checkout-flow.service';
import { CheckoutService } from '../../checkout/checkout.service';
import { SettingsService } from '../../core/settings.service';
import { IStep } from '../../stepper/stepper.component';
import { KEY_CODES } from '../../utils/helpers/feature-detection.service';

@Component({
    selector: 'app-checkout-flow',
    template: `
        <section class="full-page-takeover__content">
            <header class="full-page-takeover-header">
                <div class="full-page-takeover-header-titles">
                    <app-dynamic-value [isLoading]="!checkoutData">
                        <ng-container *ngIf="checkoutData">
                            <div class="full-page-takeover-header-titles__title">{{ checkoutData.product?.make }} {{ checkoutData.product?.model }}</div>
                            <div class="full-page-takeover-header-titles__subtitle">
                                {{ checkoutData.product?.variant }}
                            </div>
                        </ng-container>
                    </app-dynamic-value>
                </div>

                <ng-container *ngIf="breakpointService.matchBreakpoint(1024) | async">
                    <app-stepper
                        class="full-page-takeover-header__progress-bar"
                        *ngIf="checkoutFlow$ | async as flow"
                        [steps]="flow"
                        [index]="stepIndex"
                        [clickEnabled]="true"
                        (stepClicked)="handleStepClick($event)"
                    >
                    </app-stepper>
                </ng-container>

                <div class="full-page-takeover-header-close">
                    <app-button buttonClass="full-page-takeover-header-close-button button button--round-with-icon" (click)="handleOverlayClose()">
                        <i class="button__icon">${closeLightIcon}</i>
                    </app-button>
                </div>
            </header>

            <section class="flow">
                <ng-container *ngIf="checkoutData">
                    <ng-container *ngIf="!(breakpointService.matchBreakpoint(1024) | async)">
                        <app-stepper
                            class="full-page-takeover-header__progress-bar"
                            *ngIf="checkoutFlow$ | async as flow"
                            [steps]="flow"
                            [index]="stepIndex"
                            [clickEnabled]="true"
                            (stepClicked)="handleStepClick($event)"
                        >
                        </app-stepper>
                    </ng-container>
                    <ng-container [ngSwitch]="currentCheckoutStep">
                        <app-checkout-step-payment *ngSwitchCase="checkoutStepType.CS_PAYMENT"
                            [loanData]="initialLoanData"
                            [leasingData]="initialLeasingData"
                            [overlayData]="data">
                        </app-checkout-step-payment>
                        <app-checkout-step-exchange-car *ngSwitchCase="checkoutStepType.CS_EXCHANGE_CAR"></app-checkout-step-exchange-car>
                        <app-checkout-step-insurance *ngSwitchCase="checkoutStepType.CS_INSURANCE"></app-checkout-step-insurance>
                        <app-checkout-step-delivery *ngSwitchCase="checkoutStepType.CS_DELIVERY"></app-checkout-step-delivery>
                        <app-checkout-step-personal-information *ngSwitchCase="checkoutStepType.CS_PERSONAL_INFO"></app-checkout-step-personal-information>
                        <app-checkout-step-summary *ngSwitchCase="checkoutStepType.CS_SUMMARY"> </app-checkout-step-summary>
                    </ng-container>
                </ng-container>

                <section
                    *ngIf="
                        !!(checkoutService.showGenericCheckoutError$ | async) &&
                        !(checkoutService.isCheckoutLoading$ | async) &&
                        (settingsService.get() | async) as ss
                    "
                >
                    <div class="grid-spacing-small">
                        <app-rich-text [html]="ss.checkout.genericCheckoutError"></app-rich-text>
                    </div>
                    <div class="center grid-spacing-normal">
                        <app-button
                            *ngIf="!!(checkoutService.isBasketOnBehalf$ | async)"
                            (handleClick)="
                                checkoutService.initCheckoutAsBaskerOnBehalf(data?.basketOnBehalfInitData?.offerId, data?.basketOnBehalfInitData?.sid)
                            "
                            [loading]="!!(checkoutService.isCheckoutLoading$ | async)"
                            [title]="'buttons.try_again' | translate"
                        ></app-button>
                        <app-button
                            *ngIf="!(checkoutService.isBasketOnBehalf$ | async)"
                            (handleClick)="checkoutService.initCheckout(data.checkoutInitData)"
                            [loading]="!!(checkoutService.isCheckoutLoading$ | async)"
                            [title]="'buttons.try_again' | translate"
                        ></app-button>
                    </div>
                </section>

                <app-component-loader *ngIf="!!(checkoutService.isCheckoutLoading$ | async) && !checkoutData"></app-component-loader>
            </section>
        </section>

        <app-checkout-footer></app-checkout-footer>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutFlowComponent implements OnInit, OnDestroy {
    checkoutData?: CheckoutGetPurchaseOrderResponse;
    checkoutStepType = CHECKOUT_STEP;
    currentCheckoutStep?: CHECKOUT_STEP;
    checkoutFlow$ = this.checkoutFlowService.checkoutFlowOrder$;
    steps: IStep[];
    stepIndex: number;
    initialLoanData?: LoanResponse;
    initialLeasingData: any;
    private readonly unsubscribe = new Subject<void>();

    @HostListener('window:keyup', ['$event'])
    keyEvent(event: KeyboardEvent) {
        if (event.key === KEY_CODES.ESCAPE) {
            this.handleOverlayClose();
        }
    }

    constructor(
        public checkoutService: CheckoutService,
        public checkoutFlowService: CheckoutFlowService,
        public overlayRef: ImpactOverlayRef,
        public breakpointService: BreakpointService,
        public settingsService: SettingsService,
        private cdr: ChangeDetectorRef,
        private router: Router,

        @Inject(DOCUMENT) private document: Document,
        @Inject(IMPACT_OVERLAY_DATA) public data: any
    ) {
        this.checkoutService.checkoutFinalized$.pipe(takeUntil(this.unsubscribe)).subscribe((closeOverlay: boolean) => {
            if (closeOverlay) {
                this.handleOverlayClose();
            }
        });
    }

    ngOnInit() {
        this.checkoutService
            .getCheckoutData()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((res) => {
                this.checkoutData = res;
                this.cdr.markForCheck();
            });

        if (this.data.checkoutInitData.leasingData) {
            this.initialLeasingData = this.data.checkoutInitData.leasingData;
        }

        this.cdr.markForCheck();

        this.checkoutFlowService.currentCheckoutStep$.pipe(takeUntil(this.unsubscribe)).subscribe((step) => {
            this.currentCheckoutStep = step;
            this.stepIndex = this.checkoutFlowService.checkoutFlowOrder$.value?.findIndex((s) => s.type === step) ?? 0;
            this.cdr.markForCheck();

            if (this.document) {
                // Scroll to top, on each step change
                const overlayElement = this.document?.getElementsByClassName('impact-panel');
                overlayElement?.[0]?.scroll(0, 0);
            }
        });

        this.initialLoanData = this.data?.checkoutInitData?.loanData;
    }

    handleOverlayClose() {
        if (this.data.navigateToFrontpageOnClose) {
            // If checkout is opened as a Basket on behalf, SPA should route to frontpage in close.
            this.router.navigate(['/']);
        }
        this.overlayRef.close();
    }

    handleStepClick(step: IStep) {
        if (step.type) {
            this.checkoutFlowService.goToStep(step.type as CHECKOUT_STEP);
        }
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
